import type { GolfLeaderboardPlayerEntryFragment } from '../../__generated__/hooks'
import DetailSheet from './DetailSheet'
import MiniScorecard, { type CourseHoleSummary } from './MiniScorecard'

/**
 * Golf / Stableford detail sheet. Hero: rank + name + primary + secondary.
 * The headline follows the ranking — under Stableford the points are the
 * primary number (the par-relative score drops to the subscript line);
 * under net-strokes the par-relative score stays primary. Body: hole-by-hole
 * scorecard. No chip economy — golf events don't have a chip stack.
 */
const GolfDetail = ({
  open,
  entry,
  isNet,
  courseHoles,
  onClose,
}: {
  open: boolean
  entry: GolfLeaderboardPlayerEntryFragment | null
  /** Net-strokes ranking → par-relative score leads; else points lead. */
  isNet: boolean
  courseHoles: readonly CourseHoleSummary[]
  onClose: () => void
}) => {
  if (!entry) return null
  const flightNum = entry.player.flight?.flightNumber
  const subtitleParts: string[] = [`${entry.holesRegistred} spilte hull`]
  if (flightNum != null) subtitleParts.push(`flight #${flightNum}`)
  if (entry.player.hcp != null) subtitleParts.push(`hcp ${entry.player.hcp}`)
  const subtitle = subtitleParts.join(' · ')
  let formattedScore: string
  if (entry.score === 0) formattedScore = 'E'
  else if (entry.score > 0) formattedScore = `+${entry.score}`
  else formattedScore = String(entry.score)
  const strokes = entry.strokesNet || entry.strokes
  const primary = isNet ? (
    formattedScore
  ) : (
    <>
      {entry.points}
      <span className="text-sm font-bold text-ink-500 ml-1">pts</span>
    </>
  )
  const secondary = isNet
    ? `${entry.points} pts · ${strokes} slag`
    : `${formattedScore} · ${strokes} slag`
  return (
    <DetailSheet
      open={open}
      onClose={onClose}
      rank={entry.rank}
      title={entry.player.name}
      subtitle={subtitle}
      primary={primary}
      secondary={secondary}
    >
      <section className="rounded-xl bg-paper shadow-sm overflow-hidden">
        <div className="px-3 pt-3 pb-1 up text-ink-500 text-[10px]">
          Scorekort
        </div>
        <MiniScorecard
          courseHoles={courseHoles}
          scores={entry.scores}
          isNet={isNet}
          strokesAllowance={entry.player.strokes}
        />
      </section>
    </DetailSheet>
  )
}

export default GolfDetail
